import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import {
  LuLibrary, LuPlus, LuPencil, LuTrash2, LuDownload, LuExternalLink, LuSearch, LuCheck, LuUpload, LuLink, LuEye, LuEyeOff,
} from 'react-icons/lu';
import { Modal, Badge, errorMessage, fmtDate, readFileAsDataUrl } from './trainingUtils';
import { materialType, toneStyle, fmtSize, openMaterial } from './supportMaterialsUtils';
import './training.css';

/**
 * Materiais de Apoio — gestão (exclusiva do administrador global).
 * Biblioteca de arquivos/links para mentorados, separada dos materiais das
 * aulas dos cursos. Os colaboradores só veem os materiais ATIVOS, e apenas
 * se a empresa tiver o módulo "Materiais de Apoio" habilitado em
 * "Módulos por Empresa".
 */

const STATUS = {
  active: { label: 'Visível', bg: 'var(--success-soft)', color: 'var(--success)' },
  inactive: { label: 'Oculto', bg: 'var(--surface-3)', color: 'var(--text-3)' },
};

function MaterialModal({ editing, categories, options, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    title: editing?.title || '',
    description: editing?.description || '',
    category: editing?.category || '',
    active: editing ? editing.active : true,
    mode: editing?.kind === 'link' ? 'link' : 'file',
    external_url: editing?.external_url || '',
    file: null, // { name, size, dataUrl } — novo arquivo escolhido
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const maxMb = options?.max_file_mb || 20;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > maxMb * 1024 * 1024) { setError(`O arquivo passa do limite de ${maxMb} MB.`); return; }
    setError('');
    const dataUrl = await readFileAsDataUrl(file);
    setForm((f) => ({ ...f, file: { name: file.name, size: file.size, dataUrl }, title: f.title || file.name.replace(/\.[^.]+$/, '') }));
  };

  const handleSave = async () => {
    if (!form.title.trim()) { setError('Informe o título do material.'); return; }
    const isNew = !editing;
    const payload = { title: form.title, description: form.description, category: form.category, active: form.active };
    if (form.mode === 'file') {
      if (form.file) Object.assign(payload, { file_name: form.file.name, file_data: form.file.dataUrl });
      else if (isNew || editing.kind === 'link') { setError('Escolha o arquivo para enviar.'); return; }
    } else {
      if (!/^https?:\/\//i.test(form.external_url.trim())) { setError('Informe um link começando com http:// ou https://'); return; }
      payload.external_url = form.external_url.trim();
    }
    setSaving(true); setError('');
    try {
      if (isNew) await api.post('/support-materials', payload);
      else await api.put(`/support-materials/${editing.id}`, payload);
      onSaved(isNew ? 'Material cadastrado.' : 'Material atualizado.');
    } catch (e) {
      setError(errorMessage(e)); setSaving(false);
    }
  };

  return (
    <Modal title={editing ? 'Editar material' : 'Novo material de apoio'} icon={<LuLibrary color="var(--accent)" />} onClose={onClose} maxWidth={620}>
      {error && <div className="alert-custom danger">{error}</div>}

      <div className="training-chips" style={{ marginBottom: 0 }}>
        <button type="button" className={`training-chip ${form.mode === 'file' ? 'is-active' : ''}`} onClick={() => set('mode', 'file')}><LuUpload /> Enviar arquivo</button>
        <button type="button" className={`training-chip ${form.mode === 'link' ? 'is-active' : ''}`} onClick={() => set('mode', 'link')}><LuLink /> Link externo</button>
      </div>

      {form.mode === 'file' ? (
        <div>
          <label className="form-label">Arquivo {editing?.kind === 'file' ? '(envie outro só se quiser substituir)' : '*'}</label>
          <label className="material-dropzone">
            <LuUpload size={22} />
            {form.file ? (
              <span><strong>{form.file.name}</strong> · {fmtSize(form.file.size)}</span>
            ) : editing?.kind === 'file' ? (
              <span>Atual: <strong>{editing.file_name}</strong> · {fmtSize(editing.file_size)} — clique para substituir</span>
            ) : (
              <span>Clique para escolher o arquivo</span>
            )}
            <input type="file" hidden onChange={handleFile}
              accept={(options?.allowed_extensions || []).map((x) => `.${x}`).join(',')} />
          </label>
          <div className="training-hint">
            Até {maxMb} MB. Tipos aceitos: {(options?.allowed_extensions || []).join(', ')}.
          </div>
        </div>
      ) : (
        <div>
          <label className="form-label">Link *</label>
          <input className="form-control" value={form.external_url} placeholder="https://… (vídeo, pasta compartilhada, site)"
            onChange={(e) => set('external_url', e.target.value)} />
        </div>
      )}

      <div>
        <label className="form-label">Título *</label>
        <input className="form-control" value={form.title} maxLength={200} onChange={(e) => set('title', e.target.value)} />
      </div>
      <div>
        <label className="form-label">Descrição</label>
        <textarea className="form-control" rows={3} value={form.description} onChange={(e) => set('description', e.target.value)}
          placeholder="Para que serve o material e quando usar" />
      </div>
      <div className="training-form-grid">
        <div>
          <label className="form-label">Categoria</label>
          <input className="form-control" list="material-categories" value={form.category} maxLength={100}
            placeholder="Ex.: Gestão de pessoas" onChange={(e) => set('category', e.target.value)} />
          <datalist id="material-categories">{categories.map((c) => <option key={c} value={c} />)}</datalist>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
          <label className="training-check">
            <input type="checkbox" checked={form.active} onChange={(e) => set('active', e.target.checked)} />
            Visível para os mentorados
          </label>
        </div>
      </div>

      <div className="training-modal-footer">
        <button type="button" className="training-btn" onClick={onClose}>Cancelar</button>
        <button type="button" className="training-btn training-btn--primary" onClick={handleSave} disabled={saving}>
          <LuCheck /> {saving ? 'Salvando…' : 'Salvar material'}
        </button>
      </div>
    </Modal>
  );
}

export default function SupportMaterialsAdminPage() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [options, setOptions] = useState(null);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');
  const [modal, setModal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, cats] = await Promise.all([
        api.get('/support-materials', { params: { search: search || undefined, category: category || undefined, status: status || undefined } }),
        api.get('/support-materials/categories'),
      ]);
      setItems(list.data);
      setCategories(cats.data);
      setError('');
    } catch (e) {
      setError(errorMessage(e, 'Erro ao carregar os materiais'));
    } finally {
      setLoading(false);
    }
  }, [search, category, status]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { api.get('/support-materials/options').then((r) => setOptions(r.data)).catch(() => {}); }, []);

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3500); };

  const handleOpen = async (m) => {
    setError('');
    try { await openMaterial('/support-materials', m); } catch (e) { setError(e.message); }
  };

  const handleToggleActive = async (m) => {
    try {
      await api.put(`/support-materials/${m.id}`, { active: !m.active });
      flash(m.active ? `"${m.title}" ocultado dos mentorados.` : `"${m.title}" visível para os mentorados.`);
      load();
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`Excluir "${m.title}" definitivamente? O arquivo será apagado.`)) return;
    try {
      await api.delete(`/support-materials/${m.id}`);
      flash(`"${m.title}" excluído.`);
      load();
    } catch (e) {
      setError(errorMessage(e, 'Erro ao excluir'));
    }
  };

  const totalSize = items.reduce((s, m) => s + (m.file_size || 0), 0);

  return (
    <div className="page-card">
      <div className="page-card-header">
        <div>
          <div className="page-card-title">Materiais de Apoio — gestão</div>
          <div className="page-card-subtitle">Arquivos e links da DHR para empresas mentoradas (separados dos materiais dos cursos)</div>
        </div>
        <button type="button" className="training-btn training-btn--primary" onClick={() => setModal('new')}><LuPlus /> Novo material</button>
      </div>

      {error && <div className="alert-custom danger">{error}</div>}
      {success && <div className="alert-custom success">{success}</div>}
      <div className="training-hint" style={{ marginTop: -6, marginBottom: 14 }}>
        Só os materiais <strong>visíveis</strong> aparecem para os colaboradores, e apenas nas empresas com o módulo
        "Materiais de Apoio" habilitado em <strong>Administrador Global → Módulos por Empresa</strong>.
      </div>

      <div className="training-toolbar">
        <div style={{ position: 'relative', flex: '1 1 240px', maxWidth: 360 }}>
          <LuSearch style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }} />
          <input className="form-control" style={{ paddingLeft: 32, maxWidth: 'none' }} placeholder="Buscar por título, descrição ou arquivo"
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <select className="form-control" value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">Todas as categorias</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className="form-control" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Visíveis e ocultos</option>
          <option value="active">Só visíveis</option>
          <option value="inactive">Só ocultos</option>
        </select>
      </div>

      {loading && !items.length && <div className="training-hint">Carregando…</div>}
      {!loading && !items.length && (
        <div className="empty-state">
          <LuLibrary size={36} />
          <p>{search || category || status ? 'Nenhum material com esses filtros' : 'Nenhum material cadastrado ainda'}</p>
          {!search && !category && !status && <small>Clique em "Novo material" para enviar o primeiro arquivo.</small>}
        </div>
      )}

      {items.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table className="training-table">
            <thead>
              <tr><th>Material</th><th>Categoria</th><th>Tipo</th><th>Tamanho</th><th>Situação</th><th>Atualizado</th><th /></tr>
            </thead>
            <tbody>
              {items.map((m) => {
                const t = materialType(m);
                return (
                  <tr key={m.id}>
                    <td>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                        <span className="material-type-icon" style={toneStyle(t.tone)}>{t.icon}</span>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600, color: 'var(--text-1)' }}>{m.title}</div>
                          <div className="training-hint" style={{ marginTop: 0 }}>{m.kind === 'link' ? m.external_url : m.file_name}</div>
                        </div>
                      </div>
                    </td>
                    <td>{m.category || '—'}</td>
                    <td>{t.label}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{m.kind === 'link' ? '—' : fmtSize(m.file_size)}</td>
                    <td><Badge meta={STATUS[m.active ? 'active' : 'inactive']} /></td>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(m.updatedAt)}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button type="button" className="training-icon-btn" title={m.kind === 'link' ? 'Abrir link' : 'Baixar'} onClick={() => handleOpen(m)}>
                        {m.kind === 'link' ? <LuExternalLink /> : <LuDownload />}
                      </button>
                      <button type="button" className="training-icon-btn" title={m.active ? 'Ocultar dos mentorados' : 'Tornar visível'} onClick={() => handleToggleActive(m)}>
                        {m.active ? <LuEyeOff /> : <LuEye />}
                      </button>
                      <button type="button" className="training-icon-btn" title="Editar" onClick={() => setModal(m)}><LuPencil /></button>
                      <button type="button" className="training-icon-btn training-icon-btn--danger" title="Excluir" onClick={() => handleDelete(m)}><LuTrash2 /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="training-hint" style={{ padding: '10px 4px' }}>
            {items.length} material(is) · {fmtSize(totalSize)} em arquivos
          </div>
        </div>
      )}

      {modal && (
        <MaterialModal
          editing={modal === 'new' ? null : modal}
          categories={categories}
          options={options}
          onClose={() => setModal(null)}
          onSaved={(msg) => { setModal(null); flash(msg); load(); }}
        />
      )}
    </div>
  );
}
